import React from "react";
import { Link } from "react-router-dom";
import Nav from "../HomePage/Nav";
import Footer from "../HomePage/Footer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMountainSun,
  faMotorcycle,
  faHeadset,
} from "@fortawesome/free-solid-svg-icons";
import bg from "../../assets/Images/vamshi-vangapally-fH-BSvYeX5g-unsplash.jpg";
import Seo from "../Seo/Seo";
import { SITE_NAME } from "../Seo/seoConfig";

// Shown for any URL that doesn't match a route, and reused by
// PackageDestination when a /:slug has no package behind it (that catch-all
// route swallows every unknown top-level URL, so the router's "*" route would
// otherwise almost never be reached).
const QUICK_LINKS = [
  { to: "/all-destinations", label: "Destinations", icon: faMountainSun },
  { to: "/bike-package", label: "Bike Trips", icon: faMotorcycle },
  { to: "/contact-us", label: "Contact Us", icon: faHeadset },
];

const NotFound = () => {
  return (
    <>
      <Seo
        title={`Page Not Found (404) | ${SITE_NAME}`}
        description="The page you are looking for could not be found. Explore our Ladakh tour packages, destinations and stays instead."
        noindex
      />

      <style>
        {`
          .nf404-hero {
            position: relative;
            min-height: 100vh;
            width: 100%;
            background-image: url(${bg});
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;
          }

          .nf404-hero::before {
            content: "";
            position: absolute;
            inset: 0;
            background: linear-gradient(
              180deg,
              rgba(0, 0, 0, 0.65) 0%,
              rgba(3, 4, 94, 0.72) 100%
            );
            z-index: 1;
          }

          .nf404-content {
            position: relative;
            z-index: 2;
          }

          .nf404-code {
            font-size: clamp(96px, 22vw, 220px);
            font-weight: 800;
            line-height: 1;
            letter-spacing: 4px;
            color: transparent;
            -webkit-text-stroke: 2px rgba(255, 255, 255, 0.65);
            text-shadow: 0 10px 60px rgba(6, 30, 98, 0.45);
            margin-bottom: 0;
          }

          .nf404-title {
            font-size: clamp(26px, 5vw, 46px);
            font-weight: 600;
            line-height: 1.15;
          }

          .nf404-text {
            max-width: 620px;
            color: rgba(255, 255, 255, 0.85);
          }

          .nf404-rule {
            width: 60px;
            border: 2px solid #ffffff;
            opacity: 1;
          }

          .nf404-btn {
            min-width: 190px;
            border-radius: 30px;
            padding: 12px 26px;
            font-weight: 600;
          }

          .nf404-btn-light {
            background-color: #ffffff;
            color: #03045e;
            border: 2px solid #ffffff;
          }

          .nf404-btn-light:hover {
            background-color: #03045e !important;
            border-color: #ffffff !important;
            color: #ffffff !important;
          }

          .nf404-btn-outline {
            background-color: transparent;
            color: #ffffff;
            border: 2px solid #ffffff;
          }

          .nf404-btn-outline:hover {
            background-color: #ffffff !important;
            border-color: #ffffff !important;
            color: #03045e !important;
          }

          /* A global rule in index.css forces --bs-gutter-x: 0 !important on
             every .row, so Bootstrap's g-* gutters can't be used here. This
             grid owns its own spacing instead. */
          .nf404-links {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 12px;
            width: 100%;
            margin: 0;
          }

          .nf404-links > * {
            flex: 0 1 calc((100% - 12px) / 2);
            max-width: calc((100% - 12px) / 2);
          }

          /* Qualified with the tag so this outranks any global "<x> a"
             rule that might otherwise repaint the label/icon. */
          a.nf404-link-card {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 10px;
            height: 100%;
            min-height: 104px;
            padding: 20px 12px;
            border-radius: 12px;
            text-decoration: none;
            color: #ffffff;
            background-color: rgba(255, 255, 255, 0.1);
            border: 1px solid rgba(255, 255, 255, 0.25);
            transition: 0.3s ease-in-out;
          }

          a.nf404-link-card:hover,
          a.nf404-link-card:focus-visible {
            color: #03045e;
            background-color: #ffffff;
            box-shadow: 0 10px 60px 0 rgba(6, 30, 98, 0.2);
          }

          .nf404-link-card svg,
          .nf404-link-card span {
            color: inherit;
          }

          .nf404-link-card span {
            font-size: 14px;
            font-weight: 600;
            text-align: center;
          }

          /* svh keeps the hero from overflowing behind mobile browser chrome */
          @supports (min-height: 100svh) {
            .nf404-hero {
              min-height: 100svh;
            }
          }

          @media (max-width: 575.98px) {
            .nf404-code {
              letter-spacing: 2px;
              -webkit-text-stroke-width: 1.5px;
            }

            a.nf404-link-card {
              min-height: 92px;
              padding: 16px 10px;
            }

            .nf404-link-card span {
              font-size: 13px;
            }
          }

          @media (max-width: 767.98px) {
            .nf404-btn {
              width: 100%;
            }
          }

          /* 3 across on tablets */
          @media (min-width: 576px) {
            .nf404-links {
              gap: 14px;
            }

            .nf404-links > * {
              flex: 0 1 calc((100% - 28px) / 3);
              max-width: calc((100% - 28px) / 3);
            }
          }

          /* One centred row from large screens up, capped so three cards
             don't stretch across the full container. */
          @media (min-width: 992px) {
            .nf404-links {
              gap: 16px;
            }

            .nf404-links > * {
              flex: 0 1 220px;
              max-width: 220px;
            }
          }
        `}
      </style>

      <div className="nf404-hero">
        <div className="nf404-content">
          <Nav />

          <div className="container text-white py-5">
            <div className="row justify-content-center text-center">
              <div className="col-12 col-lg-10 d-flex flex-column align-items-center pt-4 pt-md-5">
                <p className="nf404-code">404</p>

                <hr className="nf404-rule my-4" />

                <h1 className="nf404-title mb-3">
                  <b>Looks like you've wandered off the trail</b>
                </h1>

                <p className="nf404-text mb-4">
                  The page you're looking for doesn't exist, may have moved, or
                  the link you followed is no longer valid. Let's get you back
                  on the road to Ladakh.
                </p>

                <div className="d-flex flex-column flex-md-row gap-3 justify-content-center w-100 mb-5">
                  <Link to="/" className="btn nf404-btn nf404-btn-light">
                    Back to Home
                  </Link>
                  <Link to="/all-packages" className="btn nf404-btn nf404-btn-outline">
                    Explore Packages
                  </Link>
                </div>

                <p className="fs-14 mb-3 text-uppercase" style={{ letterSpacing: "2px" }}>
                  Or pick up from here
                </p>

                <div className="nf404-links">
                  {QUICK_LINKS.map((link) => (
                    <div key={link.to}>
                      <Link to={link.to} className="nf404-link-card">
                        <FontAwesomeIcon icon={link.icon} style={{ fontSize: "22px" }} />
                        <span>{link.label}</span>
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default React.memo(NotFound);
